/**
 * SearchInput
 *
 * A reusable search input dropdown component for selecting items, such as countries or phone codes.
 *
 * Props:
 * - searchList: ModalPhoneNumberInputSearchItem[]
 *      Array of items to display in the dropdown. Each item should have:
 *        - value: string | number
 *        - NameEn: string (English name)
 *        - NameAr: string (Arabic name)
 *        - Flag?: string (optional image URL)
 * - placeholder: string
 *      Text to display when no value is entered (currently not used in input field in this component, can be added).
 * - onSelectCountry: (item: ModalPhoneNumberInputSearchItem) => void
 *      Callback invoked when a user clicks on an item.
 *
 * State:
 * - filteredItems: ModalPhoneNumberInputSearchItem[]
 *      Tracks the items displayed in the dropdown. Initialized with `searchList`.
 *
 * Features:
 * - Displays a scrollable dropdown of items.
 * - Shows flag image if available.
 * - Supports multi-language (Arabic/English) based on `i18n.language`.
 * - Hover effect for better UX.
 *
 * Example usage:
 * <SearchInput
 *   searchList={[{ value: 1, NameEn: "United States", NameAr: "الولايات المتحدة", Flag: "us.png" }]}
 *   placeholder="Select a country"
 *   onSelectCountry={(country) => console.log(country)}
 * />
 */

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ModalPhoneNumberInputSearchInputProps,
  ModalPhoneNumberInputSearchItem,
} from "../../types/Search";
import {
  ArabicCode,
  BASE_Images_API_URL,
} from "../../utils/constants/CommonConstants";
import GeneralImage from "../GeneralComponents/GeneralImage";

const SearchInput: React.FC<ModalPhoneNumberInputSearchInputProps> = ({
  searchList = [],
  placeholder,
  onSelectCountry = () => {},
}) => {
  const { i18n } = useTranslation();
  const [filteredItems, setFilteredItems] =
    useState<ModalPhoneNumberInputSearchItem[]>(searchList);

  return (
    <div className="w-full">
      {filteredItems.length > 0 && (
        <ul className="custom-sidebar max-h-72 overflow-y-auto rounded-xl bg-white">
          {filteredItems.map((country: any) => {
            return (
              <li
                key={country.value}
                className="cursor-pointer px-4 py-2 hover:bg-gray-200"
                onClick={() => onSelectCountry(country)}
              >
                <div className="flex items-center gap-2">
                  {country.Flag && (
                    <GeneralImage
                      src={`${BASE_Images_API_URL}/${country?.Flag}`}
                      alt={country.Name}
                      loading="lazy"
                      className="mr-2 h-7 w-7"
                    />
                  )}
                  {i18n.language === ArabicCode
                    ? country?.NameAr
                    : country?.NameEn}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default SearchInput;
